// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useMemo } from 'react';

import { api } from '../../../api/client';
import type { PipelineLogEntry, StageInfo } from '../../../api/types';
import { useAsync } from '../../../app/useAsync';
import { TextLink } from '../../TextLink';
import { pipelineRows, type PipelineRow } from './settingsState';
import styles from './ModelSettings.module.css';

/** Danish names for the stage tokens the pipeline is known to use; others are shown as they are. */
const STAGE_LABEL: Record<string, string> = {
  import: 'Import',
  slam: 'Registrering (SLAM)',
  register: 'Registrering',
  registration: 'Registrering',
  clouds: 'Punktsky',
  planes: 'Planer',
  segment: 'Segmentering',
  segmentation: 'Segmentering',
  sam3: 'Segmentering (SAM3)',
  instances: 'Instanser',
  materials: 'Materialer',
  rooms: 'Rum',
  mesh: 'Mesh',
  gsplat: 'Gaussian splat',
  align: 'Justering af 360°',
};

const STATUS_LABEL: Record<NonNullable<PipelineRow['status']>, string> = {
  running: 'kører',
  success: 'gennemført',
  failed: 'fejlede',
  cancelled: 'annulleret',
};

function stageLabel(stage: string): string {
  return STAGE_LABEL[stage] ?? stage.charAt(0).toUpperCase() + stage.slice(1).replace(/_/g, ' ');
}

/**
 * Ekspert › Pipeline: each stage with its latest run (from `/stages` and
 * `/pipeline-log`), and the way to the expert pages that run them. A failed
 * read is said quietly — the model itself does not depend on it.
 */
export function PipelineSection() {
  const read = useAsync<[StageInfo[], PipelineLogEntry[]]>(
    (signal) => Promise.all([api.stages(signal), api.pipelineLog(200, signal)]),
    [],
  );
  const rows = useMemo(() => (read.data ? pipelineRows(read.data[0], read.data[1]) : []), [read.data]);

  return (
    <>
      {read.loading && !read.data && <p className={styles.note}>Indlæser pipeline…</p>}
      {read.error && <p className={styles.note}>Pipelinen kunne ikke hentes.</p>}
      {rows.length > 0 && (
        <ul className={styles.pipe}>
          {rows.map((r) => (
            <li key={r.stage} className={styles.pipeRow}>
              <span
                className={`${styles.dot} ${r.status ? styles[`dot_${r.status}`] : styles.dot_none}`}
                aria-hidden="true"
              />
              <span className={styles.pipeName}>{stageLabel(r.stage)}</span>
              <span className={styles.pipeWhen}>
                {r.when ? r.when : 'ikke kørt'}
                {r.status && r.status !== 'success' && ` · ${STATUS_LABEL[r.status]}`}
              </span>
            </li>
          ))}
        </ul>
      )}
      {read.data && rows.length === 0 && <p className={styles.note}>Ingen trin er kørt endnu.</p>}
      <div className={styles.linkRow}>
        <TextLink size="sm" to="/pipeline">
          Pipeline
        </TextLink>
        <TextLink size="sm" to="/pipeline/log">
          Kørselslog
        </TextLink>
      </div>
    </>
  );
}
